/**
 * Fleet table rules that need no React: search highlighting, the company tag, and the Excel export
 * (built from the same values the table shows). The company split and the oil status are the fleet's
 * shared rules (services/fleetStatus, services/oilStatus), re-exported here for the table.
 */
import { normalizePlate as sharedNormalizePlate } from '../../services/possessionMatcher';
import { hasCompany } from '../../services/fleetStatus';

export { hasCompany };
export { makeOilStatus } from '../../services/oilStatus';

/** The page's plate key: lower case, no spaces or signs ("AA 12345" -> "aa12345"). */
export const plateKey = (plate) => sharedNormalizePlate(plate).toLowerCase();

/** Text for use inside a RegExp: ( + * ? [ and the rest lose their meaning. */
export const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The text split into parts, each marked when it is the searched text. The search text used to go
 * into a RegExp as typed, so "(" or "+" threw during render and emptied the table; and test() on a
 * /g RegExp kept its lastIndex, so every other match was not marked (audit FLT-08).
 */
export const highlightParts = (text, highlight) => {
    const str = String(text ?? '');
    const needle = String(highlight ?? '');
    if (!needle.trim()) return [{ text: str, match: false }];
    const lower = needle.toLowerCase();
    return str.split(new RegExp(`(${escapeRegExp(needle)})`, 'i'))
        .filter((part) => part !== '')
        .map((part) => ({ text: part, match: part.toLowerCase() === lower }));
};

/** The table's "Status" column: the company the car works for, Yelo when none. */
export const companyOf = (car) => (hasCompany(car) ? String(car.remarks).trim() : 'Yelo');

/**
 * The Excel rows: the table's own columns in the table's order, with the same values - Status is
 * the company tag, Car Status the live Ejar status (with its Branch next to it) and Remarks the
 * rental usage. The file used to write the raw status under "Status" and the company under
 * "Remarks", so one header meant different data on screen and in the file (audit FLT-15).
 */
export const fleetExportRows = (cars, { kmOf, statusOf, branchOf, oilOf, usageOf }) => (cars || []).map((c, i) => ({
    '#': i + 1,
    'Class': c.category ?? '',
    'Manufacturer': c.make ?? '',
    'Model': c.model ?? '',
    'Year': c.year ?? '',
    'Plate No': c.plate_number ?? '',
    'Previous Plates': (c.previous_plates || []).join(', '),
    'Chassis No': c.vin ?? '',
    'KM': Number(kmOf(c.plate_number)) || 0,
    'Status': companyOf(c),
    'Car Status': statusOf(c.plate_number) || '',
    'Branch': branchOf(c.plate_number) || c.branch || '',
    'Reg Exp': c.registration_expiry ?? '',
    'Oil Status': oilOf(c.plate_number).label,
    'Color': c.color ?? '',
    'Rental Rate': c.rental_rate ?? '',
    'Remarks': usageOf(c) || '',
}));
